import React, { useState, useEffect, useRef } from 'react';
import useDocumentTitle from "../components/useDocumentTitle";

const Start = () => {
  useDocumentTitle("Freelance Developer || edcodehead");

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState('idle');
  const formRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const sections = ['home', 'services', 'pricing', 'reviews', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('submitting');

    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    formData.append('form-name', 'start-referral');

    const encodedBody = new URLSearchParams(
      Array.from(formData.entries()).map(([key, value]) => [key, String(value)])
    ).toString();

    fetch('/?no-cache=1', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encodedBody
    })
      .then(() => {
        setFormStatus('success');
        if (formRef.current) {
          formRef.current.reset();
        }
        setTimeout(() => setFormStatus('idle'), 5000);
      })
      .catch((error) => {
        console.error('Netlify form submit error', error);
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { name: 'Home', id: 'home' },
    { name: 'Services', id: 'services' },
    { name: 'Pricing', id: 'pricing' },
    { name: 'Reviews', id: 'reviews' },
    { name: 'Contact', id: 'contact' }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <style>{`
        .min-h-screen { min-height: 100vh; }
        .bg-white { background-color: white; }
        .fixed { position: fixed; }
        .top-0 { top: 0; }
        .left-0 { left: 0; }
        .right-0 { right: 0; }
        .z-50 { z-index: 50; }
        .transition-all { transition: all 0.3s; }
        .bg-transparent { background-color: transparent; }
        .shadow-md { box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
        .container { max-width: 1200px; margin: 0 auto; padding: 0 1rem; }
        .flex { display: flex; }
        .items-center { align-items: center; }
        .justify-between { justify-content: space-between; }
        .justify-center { justify-center: center; }
        .h-20 { height: 5rem; }
        .text-xl { font-size: 1.25rem; }
        .font-bold { font-weight: 700; }
        .text-gray-900 { color: #111827; }
        .text-white { color: white; }
        .hidden { display: none; }
        @media (min-width: 768px) {
          .md\\:block { display: block; }
          .md\\:flex { display: flex; }
        }
        .space-x-8 > * + * { margin-left: 2rem; }
        .font-medium { font-weight: 500; }
        .text-\\[\\#ffc107\\] { color: #ffc107; }
        .hover\\:text-\\[\\#ffc107\\]:hover { color: #ffc107; }
        .text-gray-700 { color: #374151; }
        .relative { position: relative; }
        .overflow-hidden { overflow: hidden; }
        .absolute { position: absolute; }
        .inset-0 { top: 0; right: 0; bottom: 0; left: 0; }
        .text-center { text-align: center; }
        .text-5xl { font-size: 3rem; line-height: 1; }
        .mb-6 { margin-bottom: 1.5rem; }
        .mb-8 { margin-bottom: 2rem; }
        .text-2xl { font-size: 1.5rem; }
        .text-gray-300 { color: #d1d5db; }
        .max-w-3xl { max-width: 48rem; }
        .mx-auto { margin-left: auto; margin-right: auto; }
        .px-8 { padding-left: 2rem; padding-right: 2rem; }
        .py-4 { padding-top: 1rem; padding-bottom: 1rem; }
        .py-20 { padding-top: 5rem; padding-bottom: 5rem; }
        .bg-\\[\\#ffc107\\] { background-color: #ffc107; }
        .text-\\[\\#0e0e17\\] { color: #0e0e17; }
        .rounded-lg { border-radius: 0.5rem; }
        .rounded-xl { border-radius: 0.75rem; }
        .rounded-2xl { border-radius: 1rem; }
        .rounded-full { border-radius: 9999px; }
        .hover\\:bg-\\[\\#ffcd38\\]:hover { background-color: #ffcd38; }
        .border-2 { border-width: 2px; }
        .border-\\[\\#ffc107\\] { border-color: #ffc107; }
        .border-gray-300 { border-color: #d1d5db; }
        .border-gray-200 { border-color: #e5e7eb; }
        .hover\\:border-\\[\\#ffc107\\]:hover { border-color: #ffc107; }
        .shadow-lg { box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); }
        .shadow-2xl { box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
        .hover\\:shadow-xl:hover { box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); }
        .p-8 { padding: 2rem; }
        .p-6 { padding: 1.5rem; }
        .mb-4 { margin-bottom: 1rem; }
        .mb-2 { margin-bottom: 0.5rem; }
        .gap-8 { gap: 2rem; }
        .gap-6 { gap: 1.5rem; }
        .gap-4 { gap: 1rem; }
        .gap-3 { gap: 0.75rem; }
        .gap-2 { gap: 0.5rem; }
        .w-full { width: 100%; }
        .bg-gray-50 { background-color: #f9fafb; }
        .text-gray-600 { color: #4b5563; }
        .leading-relaxed { line-height: 1.625; }
        .grid { display: grid; }
        @media (min-width: 768px) {
          .md\\:grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
          .md\\:grid-cols-3 { grid-template-columns: repeat(3, 1fr); }
        }
        .aspect-video { aspect-ratio: 16 / 9; }
        .bg-gradient-to-br { background-image: linear-gradient(to bottom right, var(--tw-gradient-stops)); }
        .from-\\[\\#0e0e17\\] { --tw-gradient-from: #0e0e17; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to); }
        .to-\\[\\#1a1a2e\\] { --tw-gradient-to: #1a1a2e; }
        .via-\\[\\#1a1a2e\\] { --tw-gradient-stops: var(--tw-gradient-from), #1a1a2e, var(--tw-gradient-to); }
        .w-14 { width: 3.5rem; }
        .h-14 { height: 3.5rem; }
        .w-20 { width: 5rem; }
        .h-20 { height: 5rem; }
        .bg-green-100 { background-color: #d1fae5; }
        .text-green-600 { color: #059669; }
        .text-3xl { font-size: 1.875rem; }
        .text-4xl { font-size: 2.25rem; }
        .text-lg { font-size: 1.125rem; }
        .text-sm { font-size: 0.875rem; }
        .font-semibold { font-weight: 600; }
        .space-y-6 > * + * { margin-top: 1.5rem; }
        .space-y-4 > * + * { margin-top: 1rem; }
        .space-y-2 > * + * { margin-top: 0.5rem; }
        .block { display: block; }
        .px-4 { padding-left: 1rem; padding-right: 1rem; }
        .py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
        .focus\\:border-\\[\\#ffc107\\]:focus { border-color: #ffc107; }
        .focus\\:outline-none:focus { outline: none; }
        .disabled\\:bg-gray-400:disabled { background-color: #9ca3af; }
        .disabled\\:cursor-not-allowed:disabled { cursor: not-allowed; }
        .bg-red-50 { background-color: #fef2f2; }
        .border-red-200 { border-color: #fecaca; }
        .text-red-700 { color: #b91c1c; }
        button { cursor: pointer; border: none; background: none; }
        input, select, textarea { font-family: inherit; }
        .mt-12 { margin-top: 3rem; }
        .w-12 { width: 3rem; }
        .h-12 { height: 3rem; }
        .bg-\\[\\#ffc107\\]\\/20 { background-color: rgba(255, 193, 7, 0.2); }
        .flex-shrink-0 { flex-shrink: 0; }
        .pt-8 { padding-top: 2rem; }
        .border-t { border-top-width: 1px; }
        .border-gray-800 { border-color: #1f2937; }
        .bg-gray-900 { background-color: #111827; }
        .py-12 { padding-top: 3rem; padding-bottom: 3rem; }
        .text-gray-400 { color: #9ca3af; }
        .hover\\:text-white:hover { color: white; }
        @media (min-width: 640px) {
          .sm\\:text-6xl { font-size: 3.75rem; }
          .sm\\:text-2xl { font-size: 1.5rem; }
          .sm\\:text-5xl { font-size: 3rem; }
          .sm\\:grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
          .sm\\:py-32 { padding-top: 8rem; padding-bottom: 8rem; }
          .sm\\:p-12 { padding: 3rem; }
        }
        @media (min-width: 1024px) {
          .lg\\:text-7xl { font-size: 4.5rem; }
          .lg\\:grid-cols-3 { grid-template-columns: repeat(3, 1fr); }
          .lg\\:grid-cols-4 { grid-template-columns: repeat(4, 1fr); }
        }
      `}</style>

      {/* Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <nav className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <div className="flex-shrink-0">
              <button onClick={() => scrollToSection('home')} className={`text-xl font-bold transition-all ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                Website & Mobile App Development
              </button>
            </div>

            <div className="hidden md:block">
              <div className="flex items-center space-x-8">
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className={`transition-all font-medium ${
                      activeSection === link.id
                        ? 'text-[#ffc107]'
                        : isScrolled
                        ? 'text-gray-700 hover:text-[#ffc107]'
                        : 'text-white hover:text-[#ffc107]'
                    }`}
                  >
                    {link.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative flex items-center justify-center overflow-hidden" style={{ minHeight: '100vh' }}>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom right, #0e0e17, #1a1a2e, #0e0e17)' }}></div>

        <div className="container mx-auto px-4 text-center relative" style={{ zIndex: 10 }}>
          <div className="max-w-3xl mx-auto">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6" style={{ lineHeight: '1.1' }}>
              Need a Professional Website or Mobile App?
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Hi, I'm Eddie and I'm a full-stack developer with experience in website design, mobile app development, AI and automation for small businesses.
            </p>
            <div className="flex gap-4 justify-center" style={{ flexWrap: 'wrap' }}>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-8 py-4 bg-[#ffc107] text-[#0e0e17] rounded-lg font-semibold text-lg hover:bg-[#ffcd38] transition-all shadow-lg"
              >
                Get a Quote
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="px-8 py-4 bg-transparent border-2 border-[#ffc107] text-[#ffc107] rounded-lg font-semibold text-lg hover:bg-[#ffc107] hover:text-[#0e0e17] transition-all"
              >
                View Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 sm:py-32 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">What I Do</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Focused expertise in three core areas to help your business succeed
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 bg-white rounded-xl border-2 border-gray-200 hover:border-[#ffc107] hover:shadow-xl transition-all">
              <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)' }}>
                <span style={{ fontSize: '28px' }}>💻</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Website Development</h3>
              <p className="text-gray-600 leading-relaxed">
                Professional websites and web applications built with modern technologies like React, TypeScript, and Supabase.
              </p>
            </div>

            <div className="p-8 bg-white rounded-xl border-2 border-gray-200 hover:border-[#ffc107] hover:shadow-xl transition-all">
              <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)' }}>
                <span style={{ fontSize: '28px' }}>📱</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Mobile App Development</h3>
              <p className="text-gray-600 leading-relaxed">
                Cross-platform mobile apps for iOS and Android using React Native. Write once, deploy everywhere.
              </p>
            </div>

            <div className="p-8 bg-white rounded-xl border-2 border-gray-200 hover:border-[#ffc107] hover:shadow-xl transition-all">
              <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)' }}>
                <span style={{ fontSize: '28px' }}>⚡</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AI & Automation</h3>
              <p className="text-gray-600 leading-relaxed">
                Intelligent automation solutions using n8n, API integrations, and AI tools to streamline operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 sm:py-32 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Fair & Affordable Pricing</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transparent pricing with no hidden fees
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Starter Website', price: '$300+', items: ['Responsive design', 'Contact form', 'Basic SEO'] },
              { title: 'Professional Website', price: '$500+', items: ['Everything in Starter', 'Custom features', 'CMS integration'], popular: true },
              { title: 'Mobile App MVP', price: '$500+', items: ['Cross-platform app', 'Core features', 'Backend setup'] },
              { title: 'Custom Projects', price: "Let's Talk", items: ['Complex integrations', 'Enterprise solutions', 'Ongoing support'] }
            ].map((plan, idx) => (
              <div key={idx} className={`bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all border-2 ${plan.popular ? 'border-[#ffc107]' : 'border-gray-200'}`}>
                {plan.popular && (
                  <div className="inline-block px-3 py-1 bg-[#ffc107] text-[#0e0e17] text-sm font-semibold rounded-full mb-4">
                    Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.title}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#0e0e17]">{plan.price}</span>
                </div>
                <ul className="space-y-2 text-gray-600">
                  {plan.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span style={{ color: '#ffc107' }}>✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-20 sm:py-32 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">What Clients Say</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Sarah Johnson', role: 'Founder, TechStart Inc.', text: 'Eddie built our company website and mobile app from scratch. His attention to detail was exceptional!' },
              { name: 'Michael Chen', role: 'CEO, Growth Solutions', text: 'The automation workflows Eddie set up have saved us countless hours each week.' },
              { name: 'Amanda Rodriguez', role: 'Product Manager, InnovateCo', text: 'Professional, responsive, and delivered exactly what we needed.' }
            ].map((review, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-8 shadow-lg">
                <div className="flex gap-2 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} style={{ color: '#fbbf24', fontSize: '20px' }}>★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">"{review.text}"</p>
                <div>
                  <p className="font-bold text-gray-900">{review.name}</p>
                  <p className="text-gray-600 text-sm">{review.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 sm:py-32 relative overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #0e0e17, #1a1a2e, #0e0e17)' }}>
        <div className="container mx-auto px-4 relative" style={{ zIndex: 10 }}>
          <div className="text-center mb-8">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Let's Build Something Great</h2>
            <p className="text-xl text-gray-300">
              Ready to start your project? Get in touch.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-12" style={{ maxWidth: '64rem', margin: '0 auto' }}>
            {formStatus === 'success' ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span style={{ fontSize: '40px', color: '#059669' }}>✓</span>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h3>
                <p className="text-lg text-gray-600 mb-8">
                  Your message has been received. I'll get back to you within 24 hours.
                </p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="text-[#ffc107] hover:text-[#ffcd38] font-semibold"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                name="start-referral"
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <input type="hidden" name="form-name" value="start-referral" />

                <p style={{ display: 'none' }}>
                  <label>
                    Don't fill this out: <input name="bot-field" />
                  </label>
                </p>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ffc107] focus:outline-none transition-all"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ffc107] focus:outline-none transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="projectType" className="block text-sm font-semibold text-gray-700 mb-2">Project Type *</label>
                    <select
                      id="projectType"
                      name="project_type"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ffc107] focus:outline-none transition-all"
                      style={{ backgroundColor: 'white' }}
                    >
                      <option value="">Select a type</option>
                      <option value="website">Website Development</option>
                      <option value="mobile">Mobile App</option>
                      <option value="automation">AI/Automation</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="referralSource" className="block text-sm font-semibold text-gray-700 mb-2">How did you hear about me? *</label>
                    <select
                      id="referralSource"
                      name="referral_source"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ffc107] focus:outline-none transition-all"
                      style={{ backgroundColor: 'white' }}
                    >
                      <option value="">Select an option</option>
                      <option value="google">Google/Search Engine</option>
                      <option value="social-media">Social Media</option>
                      <option value="referral">Referral/Word of Mouth</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows="6"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ffc107] focus:outline-none transition-all"
                    placeholder="Tell me about your project..."
                    style={{ resize: 'none' }}
                  ></textarea>
                </div>

                {formStatus === 'error' && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-red-700">
                    There was an error submitting your message. Please try again.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full px-8 py-4 bg-[#ffc107] text-[#0e0e17] rounded-lg font-semibold text-lg hover:bg-[#ffcd38] transition-all disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>

          <div className="mt-12 grid sm:grid-cols-2 gap-6 text-white" style={{ maxWidth: '64rem', margin: '3rem auto 0' }}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(255, 193, 7, 0.2)' }}>
                <span style={{ fontSize: '24px', color: '#ffc107' }}>✉</span>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Email</h3>
                <p className="text-gray-300">edcodehead@gmail.com</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(255, 193, 7, 0.2)' }}>
                <span style={{ fontSize: '24px', color: '#ffc107' }}>📍</span>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Location</h3>
                <p className="text-gray-300">Working remotely, available worldwide</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center gap-6" style={{ flexWrap: 'wrap' }}>
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-2">Eddie Banks</h3>
              <p className="text-gray-400">Full-Stack Developer</p>
              <p className="text-gray-400">www.EdCodeHead.com</p>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025-2026 edcodehead.com | All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Start;
